import { mediaQueryLarge, isMobileBreakpoint } from '@fc/utilities';

const BOOL_TRUE = 'true';
const BOOL_FALSE = 'false';
const ESCAPE_KEY = 'Escape';
const TOGGLE_EVENT_NAME = 'fit:collapsible-toggle';
let collapsibleId = 0;

if (typeof window !== 'undefined') {
  window.FitCollapsibleGroup = Object.assign(window.FitCollapsibleGroup || {}, {
    toggleEventName: TOGGLE_EVENT_NAME,
    handlesSingleOpen: true,
  });
}

class AccordionCustom extends HTMLElement {
  #controller = null;
  #editorSelected = false;

  get #detailsElement() {
    return this.querySelector('details');
  }

  get #summaryElement() {
    return this.#detailsElement?.querySelector('summary');
  }

  get #contentElement() {
    return this.#detailsElement?.querySelector('.details-content');
  }

  connectedCallback() {
    const details = this.#detailsElement;
    const summary = this.#summaryElement;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#syncInitialOpenState();
    this.#syncAccessibleState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal, passive: false });
    summary.addEventListener('click', this.#handleSummaryClick, { signal, passive: false });
    details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal, passive: true });
    if (window.Shopify?.designMode && this.dataset.fit === 'accordion-row') {
      this.addEventListener('shopify:block:select', this.#handleEditorBlockSelect, { signal });
      this.addEventListener('shopify:block:deselect', this.#handleEditorBlockDeselect, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  #syncInitialOpenState() {
    const mobile = isMobileBreakpoint();
    const details = this.#detailsElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    details.open = this.#editorSelected || this.#shouldOpenByDefault(mobile);
  }

  #shouldOpenByDefault(isMobile) {
    return isMobile ? this.hasAttribute('open-by-default-on-mobile') : this.hasAttribute('open-by-default-on-desktop');
  }

  #isToggleDisabled(isMobile) {
    return isMobile ? this.dataset.disableOnMobile === BOOL_TRUE : this.dataset.disableOnDesktop === BOOL_TRUE;
  }

  #isEscapeCloseEnabled() {
    return this.dataset.closeWithEscape === BOOL_TRUE;
  }

  #handleEditorBlockSelect = () => {
    this.#editorSelected = true;
    const details = this.#detailsElement;
    if (details instanceof HTMLDetailsElement) {
      details.open = true;
      this.#syncAccessibleState();
    }
  };

  #handleEditorBlockDeselect = () => {
    this.#editorSelected = false;
  };

  #handleBreakpointChange = () => {
    this.#syncInitialOpenState();
    this.#syncAccessibleState();
  };

  #handleSummaryClick = (event) => {
    if (this.#isToggleDisabled(isMobileBreakpoint())) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== ESCAPE_KEY || !this.#isEscapeCloseEnabled()) return;

    event.preventDefault();
    event.stopPropagation();

    const details = this.#detailsElement;
    if (details instanceof HTMLDetailsElement) details.open = false;
    this.#summaryElement?.focus();
  };

  #handleToggle = () => {
    const details = this.#detailsElement;
    if (!(details instanceof HTMLDetailsElement)) return;

    this.#syncAccessibleState();
    if (details.open) this.#closeSiblingRows();

    this.dispatchEvent(
      new CustomEvent(TOGGLE_EVENT_NAME, {
        bubbles: true,
        detail: {
          open: details.open,
          details,
          summary: this.#summaryElement,
          content: this.#contentElement,
        },
      }),
    );
  };

  #syncAccessibleState() {
    const details = this.#detailsElement;
    const summary = this.#summaryElement;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const toggleDisabled = this.#isToggleDisabled(isMobileBreakpoint());
    const baseId = this.#baseId();
    summary.id = summary.id || `${baseId}-summary`;
    summary.setAttribute('aria-expanded', details.open ? BOOL_TRUE : BOOL_FALSE);
    if (toggleDisabled) {
      summary.setAttribute('aria-disabled', BOOL_TRUE);
      summary.setAttribute('tabindex', '-1');
    } else if (summary.getAttribute('tabindex') === '-1') {
      summary.removeAttribute('tabindex');
      summary.removeAttribute('aria-disabled');
    } else {
      summary.removeAttribute('aria-disabled');
    }

    const content = this.#contentElement;
    if (content instanceof HTMLElement) {
      content.id = content.id || `${baseId}-content`;
      summary.setAttribute('aria-controls', content.id);
    } else {
      summary.removeAttribute('aria-controls');
    }
  }

  #closeSiblingRows() {
    const accordionRoot = this.closest('[data-single-open]');
    if (!(accordionRoot instanceof HTMLElement)) return;
    if (String(accordionRoot.dataset.singleOpen).toLowerCase() !== BOOL_TRUE) return;
    if (this.parentElement !== accordionRoot) return;

    Array.from(accordionRoot.children).forEach((child) => {
      if (!(child instanceof HTMLElement) || child === this || child.localName !== 'collapsible-group') return;

      const details = child.querySelector('details');
      if (details instanceof HTMLDetailsElement && details.open) details.open = false;
    });
  }

  #baseId() {
    if (this.dataset.fitCollapsibleId) return this.dataset.fitCollapsibleId;

    collapsibleId += 1;
    const rawId = this.dataset.blockId || this.id || `fit-collapsible-${collapsibleId}`;
    const normalized = String(rawId)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-');

    this.dataset.fitCollapsibleId = normalized || `fit-collapsible-${collapsibleId}`;
    return this.dataset.fitCollapsibleId;
  }
}

if (!customElements.get('collapsible-group')) {
  customElements.define('collapsible-group', AccordionCustom);
}
